import * as highcharts from "highcharts";
import { defaultNumericSymbols } from "../generic/highcharts-defaults";

export const Format = {
  CalculateBudgetPercentage,
  CalculateBudgetPercentageForSalesList,
  CalculatePercentageChange,
  CalculateVariation,
  FormatHighchartsAxisLabel,
  FormatHighchartsNumber,
  FormatMoney,
  FormatNaN,
  FormatNumber,
  FormatNumberByLabel,
  FormatNumberWithoutNaN,
  FormatYoY,
  GetArrowClass,
  GetArrowColor,
  GetDivStyle,
  GetLabelClass,
  GetYear,
  ParseDateWithNamedMonths,
};

function GetYear(date: string) {
  const year = date.slice(0, 4);
  return +year;
}

function ParseDateWithNamedMonths(date: string) {
  const dateParts = date.split(" ");
  let day = 1;
  let baseIndex = 0;
  if (dateParts.length === 3) {
    day = Number(dateParts[baseIndex]);
    baseIndex++;
  }

  const months = [
    "jan",
    "feb",
    "mar",
    "apr",
    "may",
    "jun",
    "jul",
    "aug",
    "sep",
    "oct",
    "nov",
    "dec",
  ];
  const month = Number(months.indexOf(dateParts[baseIndex].toLowerCase()));

  const year = Number(dateParts[baseIndex + 1]);
  return new Date(year, month, day);
}

function FormatMoney(rawFigure: number, divisor: number, decimals: number) {
  const converted = rawFigure / divisor;
  if (isNaN(converted)) {
    return "--";
  }
  return decimals <= 0
    ? converted.toFixed(0).replace(/(\d)(?=(\d{3})+$)/g, "$1,")
    : converted.toFixed(decimals).replace(/(\d)(?=(\d{3})+\.)/g, "$1,");
}

function FormatNumber(rawFigure: number, divisor: number, decimals: number) {
  const converted = rawFigure / divisor;
  return +converted.toFixed(decimals);
}

function FormatNumberWithoutNaN(
  rawFigure: number,
  divisor: number,
  decimals: number
) {
  const converted = rawFigure / divisor;
  if (isNaN(converted)) {
    return "--";
  }
  return +converted.toFixed(decimals);
}

export type DivisorLabels = "K" | "M" | "B" | "k" | "m" | "b";
function FormatNumberByLabel(
  rawFigure: number,
  divisorLabel: DivisorLabels,
  decimals: number
) {
  switch (divisorLabel.toLowerCase()) {
    case "m": {
      const converted = rawFigure / 1000000;
      return +converted.toFixed(decimals);
    }
    case "b": {
      const converted = rawFigure / 1000000000;
      return +converted.toFixed(decimals);
    }
    case "k":
    default: {
      const converted = rawFigure / 1000;
      return +converted.toFixed(decimals);
    }
  }
}

function CalculateBudgetPercentage(
  ytd: number,
  budget: number,
  decimals: number
) {
  ytd = isNaN(ytd) ? 0 : ytd;
  budget = isNaN(budget) ? 0 : budget;
  if (ytd === 0 && budget > 0) {
    return "0";
  }
  if (ytd === 0 && budget === 0) {
    return "NA";
  }
  if (ytd > 0 && budget === 0) {
    return "100";
  }

  const tmp = (ytd / budget) * 100;
  return tmp.toFixed(decimals);
}

function CalculateBudgetPercentageForSalesList(
  ytd: number,
  budget: number,
  decimals: number
) {
  ytd = isNaN(ytd) ? 0 : ytd;
  budget = isNaN(budget) ? 0 : budget;
  if (ytd === 0 && budget > 0) {
    return "0";
  }
  if (ytd === 0 && budget === 0) {
    return "--";
  }
  if (ytd > 0 && budget === 0) {
    return "100";
  }

  const tmp = ((ytd - budget) / budget) * 100;
  return tmp.toFixed(decimals);
}

function CalculatePercentageChange(
  ytd: number,
  ytd1: number,
  decimals: number,
  onlyPositive: boolean = true,
  addPlusToPositive: boolean = true
) {
  if (ytd === 0 && ytd1 > 0) {
    return "-100";
  }
  if (ytd === 0 && ytd1 === 0) {
    return "0";
  }
  if (ytd > 0 && ytd1 === 0) {
    return "NA";
  }

  const tmp = (ytd / ytd1 - 1) * 100;
  if (isNaN(tmp)) {
    return "--";
  }
  const result =
    !onlyPositive || tmp > 0
      ? tmp.toFixed(decimals)
      : (tmp * -1).toFixed(decimals);
  if (onlyPositive) {
    return result;
  }
  return addPlusToPositive && tmp > 0 ? "+".concat(result) : result;
}

function CalculateVariation(num: number, num1: number) {
  if (num === undefined || num1 === undefined) {
    return 0;
  }
  if (num1 !== 0) {
    const variation = ((num - num1) / num1) * 100;
    return isNaN(variation)
      ? "--"
      : variation > 0
      ? "+" + variation.toFixed(0)
      : variation.toFixed();
  }
}

function FormatYoY(
  rawFigure: number,
  allowNegatives = false,
  addPlusToPositive = false
) {
  let fig = null;
  if (isNaN(rawFigure)) {
    return "--";
  }
  if (allowNegatives) {
    const temp = ((rawFigure < 0 ? rawFigure * -1 : rawFigure) * 100)
      .toFixed(0)
      .concat("%");
    fig = rawFigure < 0 ? "-".concat(temp) : temp;
    return addPlusToPositive && rawFigure >= 0 ? "+".concat(fig) : fig;
  }

  fig = ((rawFigure < 0 ? rawFigure * -1 : rawFigure) * 100)
    .toFixed(0)
    .concat("%");
  return addPlusToPositive && rawFigure >= 0 ? "+".concat(fig) : fig;
}

/**
 * Formats a number like Highcharts would do, taking into account numeric symbols.
 * Useful to format tooltips
 *
 * Source : defaultLabelFormatter in
 *          https://raw.githubusercontent.com/highcharts/highcharts/master/ts/parts/Axis.ts
 */
function FormatHighchartsNumber(value: number): string {
  let ret = "";
  let multi;
  const numericSymbols = defaultNumericSymbols;

  // The following line is supposed to work, but doesn't, so we we do it manually (this = Highcharts.Point)
  // this.series.yAxis.defaultLabelFormatter.call({axis: this.series.yAxis, value: this.y })

  const numericSymbolDetector = Math.abs(value);
  let i = numericSymbols && numericSymbols.length;
  if (i && numericSymbolDetector >= 1000) {
    while (i && ret === "") {
      i--;
      multi = Math.pow(1000, i + 1);
      if (
        // Only accept a numeric symbol when the distance is more
        // than a full unit. So for example if the symbol is k, we
        // don't accept numbers like 0.5k.
        numericSymbolDetector >= multi &&
        numericSymbols[i] !== null &&
        value !== 0
      ) {
        ret = `${highcharts.numberFormat(value / multi, 2)}${
          numericSymbols[i]
        }`;
      }
    }
  }
  if (ret === "") {
    // small numbers
    ret = highcharts.numberFormat(value, -1);
  }
  return ret;
}

/**
 * Formats the Axis label like Highcharts would do.
 *
 * Example :
 * yAxis: {
 *     labels: {
 *       formatter() {
 *         return `${Format.FormatHighchartsAxisLabel(this)}€`;
 *       },
 *     },
 */
function FormatHighchartsAxisLabel(
  context: Highcharts.AxisLabelsFormatterContextObject
): string {
  // @ts-ignore (defaultLabelFormatter is missing in the type declaration)
  return context.axis.defaultLabelFormatter.call(context);
}

function GetArrowClass(recent: number, past: number) {
  return recent < past ? "fa fa-long-arrow-down" : "fa fa-long-arrow-up";
}

function GetLabelClass(recent: number, past: number) {
  return recent < past ? "label label-danger" : "label label-success";
}

function GetDivStyle(showWidget: boolean) {
  return "padding: 2px 0px 2px 0px;" + (showWidget ? "" : "display: none;");
}

function GetArrowColor(recent: number, past: number) {
  return recent < past ? { color: "#b9292d" } : { color: "#35bd5b" };
}

function FormatNaN(num: number) {
  return isNaN(num) ? "--" : num;
}
